import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useRefs } from 'src/packages/shared/hooks/useRefsArray';

import type { TTabTemplateWithGSProps } from '../workspace/types';
import type { TabEntity } from 'src/entities/tab/TabEntity';

import { GroupSelectForWellList } from '../group-select/GroupSelectForWellList';
import { WellListPanelBody } from '../well-list-panel/WellListPanelBody';
import { FilterButton } from '../workbench/filter-button/FilterButton';

import styles from './WellListPanelAsPopup.module.scss';

type Props = {
  actualTab: TabEntity | null;
  onClosePopup: () => void;
  onCreateNewTabAccept?: (template: TTabTemplateWithGSProps, wellId: number | null, customTabName?: string) => void;
};

export const WellListPanelAsPopup = observer(function WellListPanelAsPopup({
  actualTab,
  onClosePopup,
  onCreateNewTabAccept,
}: Props) {
  const { refAt, valueAt, clearRefs } = useRefs<HTMLDivElement, number>();

  const ref = useRef<HTMLDivElement | null>(null);

  const { t } = useTranslation();

  useEffect(() => {
    if (actualTab && actualTab.wellListPanelStore) {
      actualTab.wellListPanelStore.setRefs(valueAt, clearRefs);
    }
  }, [actualTab, valueAt, clearRefs]);

  if (!actualTab || !actualTab.wellListPanelStore) {
    return null;
  }

  const {
    wellListEntity,
    groupSelectStore,
    createWellLogsWidget,
    createOperationalParametersWidget,
    createWellAnalyseWidget,
    createDirectionalDrillingWidget,
    createImageBroadcastWidget,
    createVideobroadcastWidget,
    createDayDepthChartWidget,
    createHeatmapWidget,
  } = actualTab;

  const store = actualTab.wellListPanelStore;

  return (
    <div ref={ref} className={styles.popupWrapper}>
      <div style={{ display: 'flex', padding: 8, lineHeight: '16px' }}>
        <GroupSelectForWellList
          groupSelectStore={groupSelectStore}
          onChangeGroup={store.setGroupForWellList}
          selectedGroup={groupSelectStore.groupManager.groups.find((group) => group.id === wellListEntity.groupId)}
          onOpenChange={(value) => store.setIsGroupSelectOpen(value)}
          open={store.isGroupSelectOpen}
        />
        <div>{t('wellList:title')}</div>
        {!store.isFilterStateEmpty && !store.isFilterWidgetExists && (
          <div style={{ marginLeft: 'auto' }}>
            <FilterButton onClick={store.createFilterWidget} />
          </div>
        )}
      </div>
      <WellListPanelBody
        store={store}
        wellListEntity={wellListEntity}
        onWellSelect={() => onClosePopup()}
        refAt={refAt}
        onCreateWellLogsWidget={createWellLogsWidget}
        onCreateOperationalParamsWidget={createOperationalParametersWidget}
        onCreateWellAnalyseWidget={createWellAnalyseWidget}
        onCreateDirectionalDrillingWidget={createDirectionalDrillingWidget}
        onCreateImageBroadcastWidget={createImageBroadcastWidget}
        onCreateVideoBroadcastWidget={createVideobroadcastWidget}
        onCreateDayDepthChartWidget={createDayDepthChartWidget}
        onCreateHeatmapWidget={createHeatmapWidget}
        onCreateTemplate={onCreateNewTabAccept}
      />
    </div>
  );
});
